import { useCallback, useEffect, useRef } from 'react';

/**
 * Observes a sentinel element and calls `onLoadMore` when it scrolls into view.
 *
 * `IntersectionObserver` is absent in jsdom and in older browsers, so the hook
 * degrades to doing nothing rather than throwing: an environment that cannot
 * observe intersections simply keeps the explicit page controls. Returning a
 * no-op callback keeps the caller's `useEffect` dependencies stable.
 */
export function useLoadMoreSentinel(options: {
  enabled: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  rootMargin?: string;
}): { sentinelRef: (node: HTMLElement | null) => void } {
  const { enabled, hasMore, onLoadMore, rootMargin = '200px' } = options;
  const observerRef = useRef<IntersectionObserver | null>(null);
  const onLoadMoreRef = useRef(onLoadMore);

  // Updated after commit rather than during render: the observer callback is
  // always asynchronous, so it can only ever see a settled value.
  useEffect(() => {
    onLoadMoreRef.current = onLoadMore;
  });

  const sentinelRef = useCallback(
    (node: HTMLElement | null) => {
      observerRef.current?.disconnect();
      if (!node || !enabled || !hasMore) return;
      if (typeof IntersectionObserver === 'undefined') return;

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) onLoadMoreRef.current();
        },
        { rootMargin },
      );
      observer.observe(node);
      observerRef.current = observer;
    },
    [enabled, hasMore, rootMargin],
  );

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return { sentinelRef };
}